import{
  freshStepUpProof,
  type MutationHooks,
}from"./identityAccessApi";


const BASE="/api/v1/provider-market";


export type ProviderMarketEnablementRow={
  provider_key?:string;
  deployment_key?:string;
  enabled?:boolean;
  source?:string;
  updated_at?:string;
};


// KNSAIX_PROVIDER_MARKET_OPENROUTER_UI_V1
export type ProviderMarketConnectionRow={
  provider_key?:string;
  connection_status?:string;
  credential_ref?:string;
  source?:string;
  connected_at?:string;
  verified_at?:string;
  updated_at?:string;
};


async function req<T>(
  path:string,
  init:RequestInit={},
):Promise<T>{
  const url=path.startsWith("/api/v1/")?path:`${BASE}${path}`;
  const response=await fetch(url,{
    credentials:"include",
    headers:{
      Accept:"application/json",
      "Content-Type":"application/json",
      ...(init.headers??{}),
    },
    ...init,
  });

  if(!response.ok){
    let detail=
      response.status===401
        ?"authentication_required"
        :response.status===403
          ?"permission_denied"
          :`request_failed_${response.status}`;

    try{
      const body=await response.json();

      if(typeof body?.detail==="string"){
        detail=body.detail;
      }else if(body?.detail){
        detail=JSON.stringify(body.detail);
      }
    }catch{}

    throw new Error(detail);
  }

  return response.json() as Promise<T>;
}


function asRecord(value:unknown):Record<string,unknown>|null{
  return value!==null
    &&typeof value==="object"
    &&!Array.isArray(value)
      ?value as Record<string,unknown>
      :null;
}


function findDeployment(
  value:unknown,
  providerKey:string,
  deploymentKey:string,
):ProviderMarketEnablementRow|null{
  if(Array.isArray(value)){
    for(const item of value){
      const found=findDeployment(
        item,
        providerKey,
        deploymentKey,
      );

      if(found)return found;
    }

    return null;
  }

  const record=asRecord(value);

  if(!record)return null;

  const provider=String(
    record.provider_key??record.provider??"",
  );

  const deployment=String(
    record.deployment_key
      ??record.model_key
      ??record.deployment
      ??"",
  );

  if(
    provider===providerKey
    &&deployment===deploymentKey
  ){
    return{
      provider_key:provider,
      deployment_key:deployment,
      enabled:
        typeof record.enabled==="boolean"
          ?record.enabled
          :undefined,
      source:
        typeof record.source==="string"
          ?record.source
          :undefined,
      updated_at:
        typeof record.updated_at==="string"
          ?record.updated_at
          :undefined,
    };
  }

  for(const child of Object.values(record)){
    const found=findDeployment(
      child,
      providerKey,
      deploymentKey,
    );

    if(found)return found;
  }

  return null;
}


function findConnection(
  value:unknown,
  providerKey:string,
):ProviderMarketConnectionRow|null{
  if(Array.isArray(value)){
    for(const item of value){
      const found=findConnection(
        item,
        providerKey,
      );

      if(found)return found;
    }

    return null;
  }

  const record=asRecord(value);

  if(!record)return null;

  const provider=String(
    record.provider_key??record.provider??"",
  );

  if(provider===providerKey){
    const status=
      typeof record.connection_status==="string"
        ?record.connection_status
        :undefined;

    if(status){
      return{
        provider_key:provider,
        connection_status:status,
        credential_ref:
          typeof record.credential_ref==="string"
            ?record.credential_ref
            :undefined,
        source:
          typeof record.source==="string"
            ?record.source
            :undefined,
        connected_at:
          typeof record.connected_at==="string"
            ?record.connected_at
            :undefined,
        verified_at:
          typeof record.verified_at==="string"
            ?record.verified_at
            :undefined,
        updated_at:
          typeof record.updated_at==="string"
            ?record.updated_at
            :undefined,
      };
    }
  }

  for(const child of Object.values(record)){
    const found=findConnection(
      child,
      providerKey,
    );

    if(found)return found;
  }

  return null;
}


export const providerMarketApi={
  revalidateModel:async(options:{providerKey:string;modelKey:string})=>
    req<Record<string,unknown>>(
      "/center/remediation/revalidate",
      {method:"POST",body:JSON.stringify({provider_key:options.providerKey,model_key:options.modelKey,request_id:crypto.randomUUID()})},
    ),

  enablements:async(
    providerKey="ollama",
    deploymentKey="qwen2.5-7b-local",
  )=>{
    const snapshot=await req<unknown>(
      "/enablements",
    );

    return{
      snapshot,
      deployment:findDeployment(
        snapshot,
        providerKey,
        deploymentKey,
      ),
    };
  },

  setDeploymentEnabled:async(
    options:{
      providerKey:string;
      deploymentKey:string;
      enabled:boolean;
      reason:string;
    },
    hooks:MutationHooks={},
  )=>{
    hooks.onPasskey?.();

    const proof=await freshStepUpProof();

    hooks.onApplying?.();

    return req<{
      updated:boolean;
      result:Record<string,unknown>;
    }>(
      "/enablements/set/verify",
      {
        method:"POST",
        body:JSON.stringify({
          ceremony_id:proof.ceremony_id,
          credential:proof.credential,
          prf_result:proof.prf_result,
          provider_key:options.providerKey,
          deployment_key:options.deploymentKey,
          enabled:options.enabled,
          reason:options.reason,
        }),
      },
    );
  },
  // Same server-side authority and Vault flow used by Harness Settings.
  saveProviderApiKey:async(options:{providerKey:string;apiKey:string;reason:string})=>{
    const proof=await freshStepUpProof();
    return req<{updated:boolean;result:{saved:boolean;provider_key:string;catalog_sync?:{state?:string;status?:string}}}>(
      "/api/v1/harness/provider-settings/key/save/verify",
      {method:"POST",body:JSON.stringify({
        provider_key:options.providerKey,api_key:options.apiKey,reason:options.reason,...proof,
      })},
    );
  },
  testAndSyncProvider:async(providerKey:string)=>{
    const proof=await freshStepUpProof();
    return req<{updated:boolean;result:{state:string;models_discovered?:number;reason?:string}}>(
      "/api/v1/harness/provider-settings/test/verify",
      {method:"POST",body:JSON.stringify({provider_key:providerKey,...proof})},
    );
  },
  prepareGatewayModel:async(options:{
    providerKey:string;upstreamModel:string;
    contextWindow:number;maxOutputTokens:number;reason:string;
  })=>{
    const proof=await freshStepUpProof();
    return req<{updated:boolean;result:{prepared:boolean;model_key:string;enabled:boolean}}>(
      "/api/v1/harness/provider-settings/models/gateway/prepare/verify",
      {method:"POST",body:JSON.stringify({
        provider_key:options.providerKey,
        upstream_model:options.upstreamModel,
        context_window:options.contextWindow,
        max_output_tokens:options.maxOutputTokens,
        reason:options.reason,...proof,
      })},
    );
  },
  catalogModels:async(providerKey:string)=>req<{models:Array<{
    provider_key:string;upstream_model:string;display_name?:string|null;
    model_key?:string|null;cost_class?:string|null;catalog_state?:string|null;
    selectable?:boolean;context_window?:number|null;
    max_output_tokens?:number|null;
  }>}>('/api/v1/harness/provider-settings/models?provider_key='+encodeURIComponent(providerKey)+'&limit=2000'),
  connections:async(
    providerKey:string,
  )=>{
    const snapshot=await req<unknown>(
      "/connections",
    );

    return{
      snapshot,
      connection:findConnection(
        snapshot,
        providerKey,
      ),
    };
  },

  connectProvider:async(
    options:{
      providerKey:string;
      credentialRef:string;
      reason:string;
    },
    hooks:MutationHooks={},
  )=>{
    hooks.onPasskey?.();

    const proof=await freshStepUpProof();

    hooks.onApplying?.();

    return req<Record<string,unknown>>(
      "/connections/connect/verify",
      {
        method:"POST",
        body:JSON.stringify({
          ceremony_id:proof.ceremony_id,
          credential:proof.credential,
          prf_result:proof.prf_result,
          provider_key:options.providerKey,
          credential_ref:options.credentialRef,
          reason:options.reason,
        }),
      },
    );
  },

};
